import React, {useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import AnalyticCard from '@deezer/next/features/Analytic/components/AnalyticCard';
import type {TrendingTrack} from '@deezer/next/services/api/Starmania/requests/analytic/types';
import {HStack, SimpleGrid, Text, VStack} from '@tempo/core';
import Thumbnail from '@components/Thumbnail';
import {NavLink} from '@tempo/nextjs';
import {Routes} from '@deezer/next/constants/routes';
import {ContentType} from '@deezer/next/services/types/contentType';

type TrendingTracksProps = {
	trendingTracks?: TrendingTrack[];
	isLoading: boolean;
	isError: boolean;
};

type TrendingTrackContentProps = {
	track: TrendingTrack;
	index: number;
};

const TrendingTrackContent = ({track, index}: TrendingTrackContentProps) => {
	const redirectUrl = useMemo(() => {
		return `${Routes.ANALYTIC_TRACK}/${track.isrc}`;
	}, [track]);

	return (
		<NavLink
			href={redirectUrl}
			position="relative"
			py={{base: 'spacing.m', md: 'spacing.l'}}
			ps={{base: 'spacing.l', md: 'spacing.xl'}}
			_hover={{
				bg: 'background.neutral.primary.hovered',
			}}
			// Styling for grid dividers is based on https://geary.co/internal-borders-css-grid/
			sx={{
				'&::before, &::after': {
					content: "''",
					position: 'absolute',
					backgroundColor: 'divider.neutral.primary.default',
					zIndex: 1,
				},
				'&::after': {
					inlineSize: '100%',
					blockSize: '1px',
					insetInlineStart: 0,
					insetBlockEnd: '-1px',
				},
				'&::before': {
					inlineSize: '1px',
					blockSize: '100%',
					insetBlockStart: 0,
					insetInlineEnd: '-1px',
				},
			}}
		>
			<HStack spacing={{base: 'spacing.m', md: 'spacing.xl'}}>
				<Text variant="caption.regular" color="text.neutral.primary.default">
					{String(index).padStart(2, '0')}
				</Text>
				<HStack pe="spacing.s" spacing={{base: 'spacing.s', md: 'spacing.m'}}>
					<Thumbnail
						picture={track.picture}
						height={{base: '32px', lg: '64px'}}
						width={{base: '32px', lg: '64px'}}
						rounded={false}
						type={ContentType.Track}
					/>
					<VStack alignItems="initial" spacing={0}>
						<Text variant="caption.strong" noOfLines={1}>
							{track.title}
						</Text>
						<Text
							variant="caption.regular"
							noOfLines={1}
							color="text.neutral.secondary.default"
						>
							{track.mainArtists[0]?.name}
						</Text>
					</VStack>
				</HStack>
			</HStack>
		</NavLink>
	);
};

const TrendingTracks = ({
	trendingTracks,
	isError,
	isLoading,
}: TrendingTracksProps) => {
	const {t} = useTranslation('d4c');

	return (
		<AnalyticCard
			hasNoData={!trendingTracks || !trendingTracks.length}
			title={t('analyticshome_text_trendingtracks_d4c-web')}
			subtitle={t('analyticshome_text_populartracksinlast24hours_d4c-web')}
			data-testid="trending-tracks"
			isLoading={isLoading}
			isError={isError}
			variant="overview"
		>
			{trendingTracks && (
				<SimpleGrid columns={{base: 2, md: 4, xl: 5}} overflow="hidden">
					{trendingTracks.map((t, i) => (
						<TrendingTrackContent key={t.id} track={t} index={i + 1} />
					))}
				</SimpleGrid>
			)}
		</AnalyticCard>
	);
};

export default TrendingTracks;
